import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerStudent, getKuwaitLocations } from '../services/api';
import { useLanguage } from '../LanguageContext';
import { getUTM } from '../utils/utm';

function RegistrationPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [locations, setLocations] = useState({});
  const [selectedArea, setSelectedArea] = useState('');
  const [locationError, setLocationError] = useState('');
  const [formData, setFormData] = useState({
    studentName: '', fatherName: '', dateOfBirth: '', nationality: 'Indian',
    gender: 'Male', area: '', location: '', addressInKuwait: '',
    contactNumber: '', contactCountryCode: '+965', 
    secondaryContactNumber: '', secondaryContactCountryCode: '+965',
    nativeAddress: '', email: '', civilIdStudent: '',
    civilIdParent: '', photo: null, transportRequired: '', transportOption: '',
    interestedInDonation: ''
  });
  const [message, setMessage] = useState('');
  const [applicationNumber, setApplicationNumber] = useState('');

  useEffect(() => {
    getKuwaitLocations()
      .then(res => {
        setLocations(res.data);
        setLocationError('');
      })
      .catch(err => {
        console.error('Failed to load locations:', err);
        setLocationError('Failed to load locations. Please refresh the page.');
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'area') {
      setSelectedArea(value);
      setFormData({ ...formData, area: value, location: '' });
    } else if (name === 'transportRequired') {
      setFormData({ ...formData, transportRequired: value, transportOption: '' });
    } else if (name === 'civilIdStudent' && value.length === 12) {
      // Extract DOB from Civil ID format: XYYMMDDXXXXX
      const year = value.substring(1, 3);
      const month = value.substring(3, 5);
      const day = value.substring(5, 7);
      const fullYear = parseInt(year) > 50 ? '19' + year : '20' + year;
      const dob = `${fullYear}-${month}-${day}`;
      setFormData({ ...formData, [name]: value, dateOfBirth: dob });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, photo: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) data.append(key, formData[key]);
    });
    
    const utmSource = getUTM();
    if (utmSource) data.append('utmSource', utmSource);

    try {
      const response = await registerStudent(data);
      setApplicationNumber(response.data.applicationNumber);
      setMessage('Registration successful! Your application number is: ' + response.data.applicationNumber);
    } catch (error) {
      setMessage('Registration failed. Please try again.');
    }
  };

  return (
    <div className="container">
      <div className="header">
        <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
          <img src="/misk_logo.png" alt="MISK Logo" style={{height: '60px'}} />
          <h1>Student Registration</h1>
        </div>
        <button className="btn btn-logout" onClick={() => navigate('/')}>Back to Home</button>
      </div>

      <div className="card">
        {locationError && (
          <div style={{padding: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '5px', marginBottom: '20px'}}>
            {locationError}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Civil ID (Student) *</label>
              <input type="text" name="civilIdStudent" required onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Nationality *</label>
              <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Student Name *</label>
              <input type="text" name="studentName" required onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Father Name *</label>
              <input type="text" name="fatherName" required onChange={handleChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth (DD/MM/YYYY) *</label>
              <input 
                type="text" 
                name="dateOfBirth" 
                placeholder="DD/MM/YYYY" 
                required 
                value={formData.dateOfBirth}
                onChange={handleChange}
                onFocus={(e) => e.target.type = 'date'}
                onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
              />
            </div>
            <div className="form-group">
              <label>Gender *</label>
              <select name="gender" required onChange={handleChange}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Primary Contact Number *</label>
              <div style={{display: 'flex', gap: '10px'}}>
                <select name="contactCountryCode" value={formData.contactCountryCode} onChange={handleChange} style={{width: '120px'}}>
                  <option value="+965">🇰🇼 +965</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+94">🇱🇰 +94</option>
                </select>
                <input type="tel" name="contactNumber" required onChange={handleChange} style={{flex: 1}} placeholder="12345678" />
              </div>
            </div>
            <div className="form-group">
              <label>Secondary Contact Number</label>
              <div style={{display: 'flex', gap: '10px'}}>
                <select name="secondaryContactCountryCode" value={formData.secondaryContactCountryCode} onChange={handleChange} style={{width: '120px'}}>
                  <option value="+965">🇰🇼 +965</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+94">🇱🇰 +94</option>
                </select>
                <input type="tel" name="secondaryContactNumber" onChange={handleChange} style={{flex: 1}} placeholder="12345678" />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Civil ID (Parent)</label>
              <input type="text" name="civilIdParent" onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" required onChange={handleChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Area in Kuwait *</label>
              <select name="area" required onChange={handleChange}>
                <option value="">Select Area</option>
                {Object.keys(locations).map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Location *</label>
              <select name="location" required onChange={handleChange} disabled={!selectedArea}>
                <option value="">Select Location</option>
                {selectedArea && locations[selectedArea]?.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Address in Kuwait *</label>
            <textarea name="addressInKuwait" rows="2" required onChange={handleChange}></textarea>
          </div>

          <div className="form-group">
            <label>Native Address</label>
            <textarea name="nativeAddress" rows="2" onChange={handleChange}></textarea>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Photo (Optional)</label>
              <input type="file" accept="image/*" onChange={handleFileChange} />
            </div>
            <div className="form-group"></div>
          </div>

          <div className="form-group" style={{marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px'}}>
            <div style={{marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px'}}>
              <span style={{fontSize: '16px', fontWeight: 'bold'}}>{t('transportRequired')} *</span>
              <label style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}}>
                <input 
                  type="radio" 
                  name="transportRequired" 
                  value="yes" 
                  required 
                  onChange={handleChange}
                />
                {t('yes')}
              </label>
              <label style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}}>
                <input 
                  type="radio" 
                  name="transportRequired" 
                  value="no" 
                  required 
                  onChange={handleChange}
                />
                {t('no')}
              </label>
            </div>
            <p style={{fontSize: '14px', lineHeight: '1.6', marginBottom: '15px', color: '#495057', backgroundColor: '#fff', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6'}}>
              {t('transportMessage')}
            </p>

            {formData.transportRequired === 'yes' && (
              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '5px', border: '1px solid #dee2e6', textAlign: 'left'}}>
                <label style={{display: 'block', marginBottom: '10px', cursor: 'pointer', textAlign: 'left'}}>
                  <input 
                    type="radio" 
                    name="transportOption" 
                    value="can_cover" 
                    required 
                    onChange={handleChange}
                  />{' '}
                  {t('transportCanCover')}
                </label>
                <label style={{display: 'block', cursor: 'pointer', textAlign: 'left'}}>
                  <input 
                    type="radio" 
                    name="transportOption" 
                    value="cannot_cover" 
                    required 
                    onChange={handleChange}
                  />{' '}
                  {t('transportCannotCover')}
                </label>
              </div>
            )}
          </div>

          <div className="form-group" style={{marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
              <span style={{fontSize: '16px', fontWeight: 'bold'}}>{t('interestedInDonation')} *</span>
              <label style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}}>
                <input 
                  type="radio" 
                  name="interestedInDonation" 
                  value="yes" 
                  required 
                  onChange={handleChange}
                />
                {t('yes')}
              </label>
              <label style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}}>
                <input 
                  type="radio" 
                  name="interestedInDonation" 
                  value="no" 
                  required 
                  onChange={handleChange}
                />
                {t('no')}
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{width: '100%', marginTop: '20px'}} disabled={applicationNumber}>
            Submit Registration
          </button>
          {applicationNumber && (
            <div style={{marginTop: '20px', padding: '20px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '5px', textAlign: 'center'}}>
              <h3 style={{color: '#155724', marginBottom: '10px'}}>Registration Successful!</h3>
              <p style={{fontSize: '16px', color: '#155724'}}>Your Application Number:</p>
              <p style={{fontSize: '32px', fontWeight: 'bold', color: '#2d5f3f', margin: '10px 0'}}>{applicationNumber}</p>
              <p style={{fontSize: '14px', color: '#155724'}}>Please save this number for future reference.</p>
              <button className="btn btn-primary" onClick={() => navigate('/')} style={{marginTop: '15px'}}>
                Back to Home
              </button>
            </div>
          )}
          {message && !applicationNumber && <p className={message.includes('successful') ? 'success' : 'error'}>{message}</p>}
        </form>
      </div>
    </div>
  );
}

export default RegistrationPage;
